<template>
  <div class="tw-container tw-mx-auto tw-p-4 tw-max-w-4xl">
    <!-- Loading icon -->
    <b-loading
      v-model="isLoading"
      :is-full-page="true"
      :can-cancel="true"
      class="tw-flex tw-justify-center tw-items-center"
    />

    <!-- Content when not loading -->
    <br>
    <br>
    <div v-if="!isLoading && questionnaire && response" class="tw-mt-4 tw-space-y-10">
      <!-- Emphasized Central Box for Risk Score, Level, and Description -->
      <div class="tw-bg-white tw-p-6 tw-rounded-lg tw-shadow-md tw-border tw-border-gray-300">
        <!-- Risk Score and Level -->
        <div class="tw-text-center tw-mb-6">
          <h2 class="tw-text-3xl tw-font-bold tw-text-gray-800 tw-mb-3">
            {{ prospectName }} Risk Level:
          </h2>
          <div class="tw-text-indigo-700 tw-font-bold tw-text-6xl tw-mt-2">
            {{ riskScore }}
            <span class="tw-text-lg tw-font-medium tw-text-gray-600">
              /100
            </span>
          </div>
          <div class="tw-mt-6 tw-text-left">
            <p class="tw-text-md tw-text-gray-700 tw-font-normal">
              {{ riskMapping.description }}
            </p>
          </div>
          <div class="tw-mt-4 tw-text-left">
            <p class="tw-text-md tw-text-gray-700 tw-font-normal">
              Your risk level makes you best suited for {{ articleForRiskLevel(riskMapping.level) }}
              <span class="tw-text-2xl tw-font-semibold tw-text-indigo-700">
                 {{ riskMapping.level }}
              </span>
              portfolio
            </p>
          </div>
        </div>
      </div>

      <!-- Retake Questionnaire Button -->
      <div class="tw-flex tw-justify-center tw-mb-6">
        <a 
          class="tw-bg-indigo-700 tw-text-white tw-font-semibold tw-py-2 tw-px-4 tw-rounded hover:tw-bg-indigo-800 tw-transition tw-duration-300 tw-ease-in-out tw-text-center tw-no-underline"
          :href="riskQuestionnaireUrl"
        >
          Retake Questionnaire
        </a>
      </div>
      <!-- <div v-if="checkupId" class="tw-flex tw-justify-center tw-mb-6">
        <a
          class="tw-bg-indigo-700 tw-text-white tw-font-semibold tw-py-2 tw-px-4 tw-rounded hover:tw-bg-indigo-800 tw-transition tw-duration-300 tw-ease-in-out tw-text-center tw-no-underline"
          :href="checkupUrl"
        >
          Go back to AdvisorCore
        </a>
      </div> -->


      <!-- Subsection Title for Questions -->
      <h3 class="tw-text-xl tw-font-semibold tw-text-gray-600 tw-mb-4 tw-text-center">
        Questionnaire Responses
      </h3>

      <!-- Questions Section - Left Aligned -->
      <div class="tw-bg-white tw-p-6 tw-rounded-lg tw-shadow tw-border tw-border-gray-300 tw-text-gray-600">
        <div v-for="(question, index) in questionnaire.questions" :key="question.slug" class="tw-mb-5 tw-last:tw-mb-0">
          <h4 class="tw-text-lg tw-font-medium tw-mb-2">
            {{ index + 1 }}. {{ question.prompt }}
          </h4>
          <ul class="tw-list-disc tw-pl-5 tw-text-sm">
            <li v-for="(choiceValue, choiceText) in question.choices" :key="choiceValue" class="tw-mb-1 tw-font-normal">
              <span :class="{'tw-font-medium tw-text-indigo-700': isSelected(question.slug, choiceValue), 'tw-font-normal': !isSelected(question.slug, choiceValue)}">
                {{ choiceText }}
              </span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RiskQnrResultsView',
  data() {
    return {
      prospectId: null,
      response: null,
      isLoading: false,
      questionnaire: null,
      prospectName: '',
      riskScore: null,
      prospectExternalId: null,
      riskQuestionnaireUrl: '',
      checkupId: null,
    };
  },
  computed: {
    riskQuestionnaireUrl: function() {
      return `${window.location.protocol}//${window.location.host}/questionnaire/questions/v2_${this.prospectExternalId}/?questionnaire_type=risk`;
    },

    riskMapping() {
      const mappings = {
        '0-20': { level: 'Conservative', description: 'You prefer to keep your investments safe, even if it means potentially lower returns.' },
        '21-40': { level: 'Moderately Conservative', description: 'You are willing to take on a little risk for some potential reward.' },
        '41-60': { level: 'Moderate', description: 'You are comfortable with a moderate level of risk for moderate potential returns.' },
        '61-80': { level: 'Moderately Aggressive', description: 'You are willing to take on significant risk for higher potential returns.' },
        '81-100': { level: 'Aggressive', description: 'You are willing to take on more risk for a potential higher reward, and you\'re okay with the volatility that comes with it.' },
      };

      for (const range in mappings) {
        const [min, max] = range.split('-').map(Number);
        if (this.riskScore >= min && this.riskScore <= max) {
          return mappings[range];
        }
      }

      return { level: 'Unknown', description: 'Risk score is not within expected range.' };
    },
  },
  async created() {
    this.prospectId = this.$route.params.prospectId;
    await this.getQuestionnaireResponses();
    await this.getQuestionnaireText();
    await this.fetchProspectNameAndRiskScore();
  },
  async mounted() {
    const urlParams = new URLSearchParams(window.location.search);
    this.checkupId = urlParams.get('checkup_id');
  },
  methods: {
    articleForRiskLevel(level) {
      // Check if the first letter of the level is a vowel
      const vowels = ['A', 'E', 'I', 'O', 'U'];
      return vowels.includes(level[0].toUpperCase()) ? 'an' : 'a';
    },
    async getQuestionnaireResponses() {
      this.isLoading = true;
      try {
        const response = await fetch(`/api/v1/risk-qnr-results/${this.prospectId}/`);
        // Check if the response is a redirect
        if (response.redirected) {
          // Follow the redirect URL
          window.location.href = response.url;
        } else {
          this.response = (await response.json()).response.answers;
        }
      } catch (error) {
        console.error('Failed to fetch questionnaire responses:', error);
      } finally {
        this.isLoading = false;
      }
    },
    async getQuestionnaireText() {
      this.isLoading = true;
      try {
        const response = await fetch(`/api/v1/questionnaire/risk/`);
        const data = await response.json();
        this.questionnaire = data.questionnaire;
      } catch (error) {
        console.error('Failed to fetch questionnaire text:', error);
      } finally {
        this.isLoading = false;
      }
    },
    isSelected(questionSlug, choiceValue) {
      const responseValue = this.response[questionSlug];
      return responseValue === Number(choiceValue);
    },
    async fetchProspectNameAndRiskScore() {
      try {
        const response = await fetch(`/api/v1/get-risk-score/${this.prospectId}/`);
        if (response.redirected) {
          window.location.href = response.url;
        } else {
          const data = await response.json();
          this.prospectName = data.person_entity_name;
          this.riskScore = data.risk_score;
          this.prospectExternalId = data.prospect_external_id;
          this.riskQuestionnaireUrl = `${window.location.protocol}//${window.location.host}/questionnaire/questions/v2_${btoa(this.prospectExternalId)}/?questionnaire_type=risk`;
        }
      } catch (error) {
        console.error('Failed to fetch prospect name and risk score:', error);
      }
    },

  },
};
</script>

<style scoped>
.selected {
  font-weight: bold;
  color: blue;
}
.risk-meter {
  position: relative;
  width: 200px;
  height: 100px;
}

.meter-track {
  stroke: #ddd;
  stroke-width: 2;
  fill: none;
}

.meter-fill {
  stroke: #4c51bf;
  stroke-width: 2;
  fill: none;
  stroke-dasharray: 44;
  transition: stroke-dashoffset 0.3s ease;
}

.risk-score {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 1.5rem;
  font-weight: bold;
  color: #4c51bf;
}
.risk-score-2 {
  font-size: 1.5rem;
  font-weight: bold;
  color: #4c51bf;
}
</style>
